import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { UtilityService } from '@app/core/services/utility.service';
import { TranslateEditDialogComponent, TranslateEditDialogData } from '@app/modal/translate-edit-dialog/translate-edit-dialog.component';

@Component({
    selector: 'tt-tabs tt-tab',
    templateUrl: './tab.component.html',
    styleUrls: ['./tab.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class TabComponent<TType extends string = ''> implements OnInit, OnChanges {
    /**
     * The id of the tab, must be unique for this tab-set. Used to signalize when this is the selected tab.
     */
    @Input()
    public ttId: TType = '' as TType;

    /**
     * The label to the display for this tab.
     */
    @Input()
    public ttLabel: string = '';

    /**
     * The displayed label.
     */
    public label = '';

    /**
     * Whether the label for this tab should be translated.
     */
    @Input()
    public set ttTranslateLabel(value: BooleanInput) {
        this._translateLabel = coerceBooleanProperty(value);
    }
    public get ttTranslateLabel(): boolean {
        return this._translateLabel;
    }
    private _translateLabel = true;

    /**
     * The icon to display for this tab.
     */
    @Input()
    public ttIcon?: string = '';

    /**
     * Whether this tab is the active tab.
     */
    @Input()
    public set ttActive(value: BooleanInput) {
        this._active = coerceBooleanProperty(value);
    }
    public get ttActive(): boolean {
        return this._active;
    }
    private _active = false;

    /**
     * Whether this tab is disabled.
     */
    @Input()
    public set ttDisabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
    }
    public get ttDisabled(): boolean {
        return this._disabled;
    }
    private _disabled = false;

    /**
     * Whether to show loading animation for this tab.
     */
    @Input()
    public set ttSkeleton(value: BooleanInput) {
        this._skeleton = coerceBooleanProperty(value);
    }
    public get ttSkeleton(): boolean {
        return this._skeleton;
    }
    private _skeleton = false;

    /**
     * Whether this tab is dismissable if the set of tabs it is a part of is dismissable. Alone a tab cannot be dismissable without its tabs parent being dismissable.
     *
     * @default true
     */
    @Input()
    public set ttDismissable(value: BooleanInput) {
        this._dismissable = coerceBooleanProperty(value);
    }
    public get ttDismissable(): boolean {
        return this._dismissable;
    }
    private _dismissable = true;

    public parentDismissable = false;

    @Output()
    public ttDismiss = new EventEmitter<MouseEvent>();

    /**
     * Event emitted when the tab is clicked.
     */
    @Output()
    public ttClick = new EventEmitter<MouseEvent>();

    /**
     * When data for the component is finished loading.
     */
    public ready = false;

    constructor(
        private translate: TranslateService,
        private datatask: DataTaskService,
        private dialog: MatDialog,
        private utility: UtilityService,
    ) {}

    private openTranslationDialog = async () => {
        if (!!this.ttLabel) {
            const edits = await this.datatask.Post(61, { word_id: this.ttLabel });
            const dialogRef = this.dialog.open(TranslateEditDialogComponent, {
                width: '120rem',
                maxWidth: '95vw',
                height: '80vh',
                data: <TranslateEditDialogData<any>>{
                    heading: `Tab - ${this.ttLabel}`,
                    textToTranslate: this.label,
                    edits: edits,
                    save: async (data) => {
                        await this.datatask.Post(62, { wordlang_names: data.edits });
                        await this.setLabelAfterEditTranslation();
                        dialogRef.close();
                    },
                },
            });
        }
    };

    /**
     * Handles click event on tabs.
     *
     * @param event the mouse event for the click event.
     */
    public onClick(event: MouseEvent) {
        if (this.utility.isModifierKeyPressed(event, true) && this.ttTranslateLabel) {
            this.openTranslationDialog();
        } else {
            this.ttClick.emit(event);
        }
    }

    public dismiss(event: MouseEvent) {
        if (!this.ttDismissable) return;

        event.stopImmediatePropagation();
        event.stopPropagation();
        event.preventDefault();

        this.ttDismiss.emit(event);
    }

    /**
     * Sets the label of the tab.
     */
    private async setLabel() {
        if (this.ttTranslateLabel) {
            try {
                this.label = await this.translate.translate(this.ttLabel);
            } catch (err) {
                this.label = this.ttLabel;
            }
        } else {
            this.label = this.ttLabel;
        }
    }

    async setLabelAfterEditTranslation() {
        for (let i = 0; i < 3; i++) {
            // @ts-ignore
            this.translate._isRefreshed = this.translate._isRefreshed.filter((word) => word !== this.ttLabel);
            this.label = await this.translate.translate(this.ttLabel);
            await new Promise<void>((resolve) => setTimeout(() => resolve(), 750));
        }
    }

    ngOnInit(): void {
        if (!this.ttId) throw Error('A unqiue id is required for tt-tab to work properly.');
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes['ttActive']) {
            this.ttActive = changes['ttActive'].currentValue;
        }

        if (changes['ttDismissable']) {
            this.ttDismissable = changes['ttDismissable'].currentValue;
        }

        if (changes['ttDisabled']) {
            this.ttDisabled = changes['ttDisabled'].currentValue;
        }

        if (changes['ttSkeleton']) {
            this.ttSkeleton = changes['ttSkeleton'].currentValue;
        }

        if (changes['ttTranslateLabel']) {
            this.ttTranslateLabel = changes['ttTranslateLabel'].currentValue;
        }

        try {
            if (!!changes?.['ttLabel']?.currentValue && typeof changes?.['ttLabel']?.currentValue === 'string' && changes?.['ttLabel']?.currentValue !== changes?.['ttLabel']?.previousValue) {
                await this.setLabel();
            }
        } finally {
            this.ready = true;
        }
    }
}
